import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { 
    Button, 
    Box, 
    Paper, 
    Typography, 
    Tabs, 
    Tab, 
    Snackbar, 
    Alert, 
    IconButton 
} from '@mui/material';
import { useSelector } from 'react-redux';
import apiClient from '../../networking/axiosconfig';

const UISettings = () => {
    const { data: config } = useSelector((state) => state.config);
    const [activeTab, setActiveTab] = useState(0);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [categoryBrands, setCategoryBrands] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const closeSnackbar = () => {
        setSnackbar(prev => ({
            ...prev,
            open: false
        }));
    };

    // Fetch data on component mount
    useEffect(() => {
        if (config.API_BASE_URL) {
            fetchCategories();
            fetchBrands();
        }
    }, [config.API_BASE_URL]);

    const fetchCategories = async () => {
        try {
            const response = await apiClient.get(`${config.API_BASE_URL}/api/categories`);
            setCategories(response.data);
            // Initialize category brands mapping
            const initialCategoryBrands = {};
            response.data.forEach(category => {
                initialCategoryBrands[category._id] = category.brands || [];
            });
            setCategoryBrands(initialCategoryBrands);
        } catch (error) {
            console.error('Error fetching categories:', error);
            showSnackbar('Failed to load categories', 'error');
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await apiClient.get(`${config.API_BASE_URL}/api/brands`);
            setBrands(response.data);
        } catch (error) {
            console.error('Error fetching brands:', error);
            showSnackbar('Failed to load brands', 'error');
        }
    };

    // Drag and drop handlers
    const handleOnDragEnd = async (result, categoryId) => {
        if (!result.destination) return;
        
        const items = Array.from(categoryBrands[categoryId] || []);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update local state
        const updatedCategoryBrands = {
            ...categoryBrands,
            [categoryId]: items
        };
        setCategoryBrands(updatedCategoryBrands);

    };

    const handleOnDragEndBrands = async (result) => {
        if (!result.destination) return;
        
        const items = Array.from(brands);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update local state
        setBrands(items);
        
        // Save to backend
        try {
            await apiClient.put(`${config.API_BASE_URL}/api/brands/reorder`, { brands: items });
            showSnackbar('Brands order updated successfully');
        } catch (error) {
            console.error('Error updating brands order:', error);
            showSnackbar('Failed to update brands order', 'error');
            // Revert local state on error
            fetchBrands();
        }
    };

    const handleOnDragEndCategories = async (result) => {
        if (!result.destination) return;
        
        const items = Array.from(categories);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update sortOrder based on new position
        const updatedItems = items.map((item, index) => ({
            ...item,
            sortOrder: index + 1
        }));
        
        // Update local state
        setCategories(updatedItems);
        
        // Save to backend
        try {
            await apiClient.put(`${config.API_BASE_URL}/api/categories/reorder`, { 
                categories: updatedItems.map(({ _id, sortOrder }) => ({ _id, sortOrder }))
            });
            showSnackbar('Categories order updated successfully');
        } catch (error) {
            console.error('Error updating categories order:', error);
            showSnackbar('Failed to update categories order', 'error');
            // Revert local state on error
            fetchCategories();
        }
    };

    // Save handlers
    const handleSaveBrands = async () => {
        try {
            setIsSaving(true);
            await apiClient.put(`${config.API_BASE_URL}/api/brands/reorder`, { brands });
            showSnackbar('Brands order saved successfully');
        } catch (error) {
            console.error('Error saving brands order:', error);
            showSnackbar('Failed to save brands order', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveCategories = async () => {
        try {
            setIsSaving(true);
            await apiClient.put(`${config.API_BASE_URL}/api/categories/reorder`, { categories });
            showSnackbar('Categories order saved successfully');
        } catch (error) {
            console.error('Error saving categories order:', error);
            showSnackbar('Failed to save categories order', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveCategoryBrands = async () => {
        try {
            setIsSaving(true);
            // Convert categoryBrands to an array of {_id, brands} objects
            const categoriesToUpdate = Object.entries(categoryBrands).map(([categoryId, brandIds]) => ({
                _id: categoryId,
                brands: brandIds
            }));
            
            await apiClient.put(`${config.API_BASE_URL}/api/categories/brands/update-orders`, { categories: categoriesToUpdate });
            showSnackbar('Category brands updated successfully');
        } catch (error) {
            console.error('Error saving category brands:', error);
            showSnackbar('Failed to update category brands', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Helper functions
    const addBrandToCategory = (categoryId, brandId) => {
        setCategoryBrands(prev => ({
            ...prev,
            [categoryId]: [...(prev[categoryId] || []), brandId]
        }));
    };

    const removeBrandFromCategory = (categoryId, brandId) => {
        setCategoryBrands(prev => ({
            ...prev,
            [categoryId]: (prev[categoryId] || []).filter(id => id !== brandId)
        }));
    };

    // Tab rendering functions
    const renderBrandChip = (categoryId, brandId, index) => {
        const brand = brands.find(b => b._id === brandId);
        if (!brand) return null;

        return (
            <Draggable
                key={brandId}
                draggableId={`${categoryId}-${brandId}`}
                index={index}
            >
                {(provided) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            mb: 1,
                            '&:hover': {
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        <Box 
                            {...provided.dragHandleProps}
                            sx={{ 
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                mr: 1,
                                color: 'text.secondary',
                                '&:active': {
                                    cursor: 'grabbing',
                                }
                            }}
                        >
                            <DragIndicatorIcon />
                        </Box>
                        <Box
                            component="img"
                            src={brand.icon}
                            alt={brand.name}
                            sx={{
                                width: 20,
                                height: 20,
                                mr: 1,
                                objectFit: 'contain'
                            }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/placeholder-icon.png';
                            }}
                        />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            {brand.name}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeBrandFromCategory(categoryId, brandId);
                            }}
                            sx={{ p: 0.5, ml: 0.5 }}
                        >
                            <LinkOffIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Draggable>
        );
    };

    const renderCategoriesTab = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" color="textSecondary">
                    Drag and drop to reorder categories.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveCategories}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save Categories Order'}
                </Button>
            </Box>
            <DragDropContext onDragEnd={handleOnDragEndCategories}>
                <Droppable droppableId="categories" direction="vertical">
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                width: '100%',
                                maxWidth: '800px',
                                p: 2,
                            }}
                        >
                            {categories.map((category, index) => (
                                <Draggable
                                    key={category._id}
                                    draggableId={`category-${category._id}`}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <Paper
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 2,
                                                mb: 2,
                                                bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
                                                borderRadius: 1,
                                                boxShadow: snapshot.isDragging ? 3 : 1,
                                            }}
                                        >
                                            <DragIndicatorIcon
                                                {...provided.dragHandleProps}
                                                sx={{ mr: 2, color: 'text.secondary' }}
                                            />
                                            <Box
                                                component="img"
                                                src={`${config.API_BASE_URL}/images/${category.icon}`}
                                                alt={category.name}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: 'contain',
                                                    mr: 2
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/placeholder-category.png';
                                                }}
                                            />
                                            <Typography variant="body1">
                                                {category.name}
                                            </Typography>
                                        </Paper>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </Box>
    );

    const renderBrandsTab = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" color="textSecondary">
                    Drag and drop to reorder brands.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveBrands}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save Brands Order'}
                </Button>
            </Box>
            <DragDropContext onDragEnd={handleOnDragEndBrands}>
                <Droppable droppableId="brands" direction="vertical">
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                width: '100%',
                                maxWidth: '800px',
                                p: 2,
                                minHeight: '200px',
                            }}
                        >
                            {brands.map((brand, index) => (
                                <Draggable 
                                    key={brand._id} 
                                    draggableId={`brand-${brand._id}`}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <Paper
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 2,
                                                mb: 2,
                                                bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
                                                borderRadius: 1,
                                                boxShadow: snapshot.isDragging ? 3 : 1,
                                            }}
                                        >
                                            <DragIndicatorIcon
                                                {...provided.dragHandleProps}
                                                sx={{ mr: 2, color: 'text.secondary' }}
                                            />
                                            <Box
                                                component="img"
                                                src={brand.icon}
                                                alt={brand.name}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: 'contain',
                                                    mr: 2
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/placeholder-icon.png';
                                                }}
                                            />
                                            <Typography variant="body1">
                                                {brand.name}
                                            </Typography>
                                        </Paper>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </Box>
    );

    const renderCategoryBrandsTab = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" color="textSecondary">
                    Manage which brands appear under each category
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveCategoryBrands}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={3}>
                {categories.map((category) => (
                    <Paper key={category._id} sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            {category.name}
                        </Typography>
                        <DragDropContext onDragEnd={(result) => handleOnDragEnd(result, category._id)}>
                            <Droppable droppableId={`category-${category._id}`} direction="vertical">
                                {(provided) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        sx={{
                                            width: '100%',
                                            minHeight: '60px',
                                            p: 1,
                                            bgcolor: 'background.default',
                                            borderRadius: 1,
                                            mb: 2
                                        }}
                                    >
                                        {(categoryBrands[category._id] || []).map((brandId, index) =>
                                            <Box key={brandId} sx={{ mb: 1 }}>
                                                {renderBrandChip(category._id, brandId, index)}
                                            </Box>
                                        )}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </DragDropContext>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Available Brands:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {brands
                                .filter(brand => !(categoryBrands[category._id] || []).includes(brand._id))
                                .map((brand) => (
                                    <Box
                                        key={brand._id}
                                        onClick={() => addBrandToCategory(category._id, brand._id)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: 'action.hover',
                                            borderRadius: 16,
                                            px: 1.5,
                                            py: 0.5,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'action.selected',
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={brand.icon}
                                            alt={brand.name}
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                mr: 1,
                                                objectFit: 'contain'
                                            }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = '/placeholder-icon.png';
                                            }}
                                        />
                                        <Typography variant="body2">{brand.name}</Typography>
                                    </Box>
                                ))}
                        </Box>
                    </Paper>
                ))}
            </Box>
        </Box>
    );

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">UI Settings</Typography>
            </Box>
            
            <Paper elevation={2} sx={{ mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Categories" />
                    <Tab label="Brands" />
                    <Tab label="Category Brands" />
                </Tabs>
            </Paper>

            <Box sx={{ p: 2 }}>
                {activeTab === 0 && renderCategoriesTab()}
                {activeTab === 1 && renderBrandsTab()}
                {activeTab === 2 && renderCategoryBrandsTab()}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UISettings;